import { THeaderType } from 'components/Header/Header'
import LinkWithLocale from 'components/LinkWithLocale'
import React, { FC, useState } from 'react'
// import BadgeWarning from 'ui/Badge'
import { useQueries } from '@tanstack/react-query'
import arrowRightDropdownItem from 'assets/svg/arrowRightDropdownItem.svg'
import { fetchEstimatedExchangeAmount } from 'src/fetch'
import { BadgeType, PairItemTypes } from 'src/types'
import styled from 'styled-components'
import DropdownFooter from '../DropdownFooter'
import PairItem from './PairItem'
import PreviewBlock from './PreviewBlock'

import useLocale from 'hooks/useLocale'
import useTranslation from 'hooks/useTranslation'
import { ReactComponent as Avax } from './svg/avax.svg'
import { ReactComponent as BTCIcon } from './svg/btc.svg'
import { ReactComponent as DaiIcon } from './svg/dai.svg'
import { ReactComponent as EthIcon } from './svg/eth.svg'
import { ReactComponent as LinkIcon } from './svg/link.svg'
import { ReactComponent as TronIcon } from './svg/trx.svg'
import { ReactComponent as UsdcEthIcon } from './svg/usdc__eth_erc20.svg'
import { ReactComponent as UsdtTronIcon } from './svg/usdt__tron_trc20.svg'
import { ReactComponent as WbtcIcon } from './svg/wbtc.svg'
import { DropdownItemName, DropdownItemText } from 'components/Header/ui'
import { GATSBY_MAIN_URL } from '@constants'

interface DropdownBuyCryptoNavItemProps extends IOwnProps {
  dropdownType:
    | 'app'
    | 'buy crypto'
    | 'futures'
    | 'spot'
    | 'explore trading'
    | 'price alerts'
    | 'staking'
    | 'markets'
    | 'refferal'
}

interface PairItemWithIconInterface extends PairItemTypes {
  isError: any
  icon: JSX.Element
  tickerLabel: string
}

const dummyOptions: PairItemWithIconInterface[] = [
  {
    label: 'Bitcoin',
    tickerLabel: 'BTC',
    coinsRequestName: 'btc',
    percent: '0.36%',
    trendRange: '24H',
    isBadge: BadgeType.Popular,
    isIndicator: true,
    isPositive: true,
    estimatedAmount: '',
    isError: '',
    icon: <BTCIcon />,
  },
  {
    label: 'TRON',
    tickerLabel: 'TRX',
    coinsRequestName: 'trx',
    percent: '0.36%',
    trendRange: '24H',
    isBadge: null,
    isIndicator: true,
    isPositive: true,
    estimatedAmount: '',
    isError: '',
    icon: <TronIcon />,
  },
  {
    label: 'Ethereum',
    tickerLabel: 'ETH',
    coinsRequestName: 'eth',
    percent: '0.36%',
    trendRange: '24H',
    isBadge: null,
    isIndicator: true,
    isPositive: true,
    estimatedAmount: '',
    isError: '',
    icon: <EthIcon />,
  },
  {
    label: 'Dai',
    tickerLabel: 'DAI',
    coinsRequestName: 'dai',
    percent: '0.36%',
    trendRange: '24H',
    isBadge: null,
    isIndicator: true,
    isPositive: true,
    estimatedAmount: '',
    isError: '',
    icon: <DaiIcon />,
  },
  {
    label: 'ChainLink',
    tickerLabel: 'LINK',
    coinsRequestName: 'link',
    percent: '0.36%',
    trendRange: '24H',
    isBadge: null,
    isIndicator: true,
    isPositive: true,
    estimatedAmount: '',
    isError: '',
    icon: <LinkIcon />,
  },
  {
    label: 'Tether',
    tickerLabel: 'USDT',
    coinsRequestName: 'usdttrc20',
    percent: '0.36%',
    trendRange: '24H',
    isBadge: BadgeType.Hot,
    isIndicator: true,
    isPositive: true,
    estimatedAmount: '',
    isError: '',
    icon: <UsdtTronIcon />,
  },
  {
    label: 'Avalanche',
    tickerLabel: 'AVAX',
    coinsRequestName: 'avaxc',
    percent: '0.36%',
    trendRange: '24H',
    isBadge: null,
    isIndicator: true,
    isPositive: true,
    estimatedAmount: '',
    isError: '',
    icon: <Avax />,
  },
  {
    label: 'USD Coin',
    tickerLabel: 'USDC',
    coinsRequestName: 'usdc',
    percent: '0.36%',
    trendRange: '24H',
    isBadge: null,
    isIndicator: true,
    isPositive: true,
    estimatedAmount: '',
    isError: '',
    icon: <UsdcEthIcon />,
  },
  {
    label: 'Wrapped Bitcoin',
    tickerLabel: 'WBTC',
    coinsRequestName: 'wbtc',
    percent: '0.36%',
    trendRange: '24H',
    isBadge: null,
    isIndicator: true,
    isPositive: true,
    estimatedAmount: '',
    isError: '',
    icon: <WbtcIcon />,
  },
]

interface CoinData {
  coinPrice: string
  coinName: string
  coinTicker: string
  coinRequestName: string
  coinIcon: JSX.Element
  isError: string
}

const DropdownBuyCryptoNavItem: FC<DropdownBuyCryptoNavItemProps> = ({ options }) => {
  const [previewCoinData, setPreviewCoinData] = useState<CoinData>({
    coinPrice: dummyOptions[0].estimatedAmount ?? '',
    coinName: dummyOptions[0].label,
    coinIcon: dummyOptions[0].icon,
    coinTicker: dummyOptions[0].tickerLabel,
    coinRequestName: dummyOptions[0].coinsRequestName ?? '',

    isError: 'couldn’t load estimate',
  })

  const { t } = useTranslation()

  useQueries({
    queries: dummyOptions.map(coin => {
      return {
        queryKey: ['user', coin.coinsRequestName],
        queryFn: () =>
          fetchEstimatedExchangeAmount(20000, 'usd', `${coin.coinsRequestName}`)
            .then(data => (coin.estimatedAmount = (20000 / data?.estimatedAmount).toFixed(2).toString()))
            .catch(() => (coin.isError = 'couldn’t load estimate')),
      }
    }),
  })

  const { locale } = useLocale()
  const buyTokenLink =
    locale === 'en'
      ? `${GATSBY_MAIN_URL}/page/buy-crypto?coin=${previewCoinData.coinRequestName}`
      : `${GATSBY_MAIN_URL}/page/${locale}/buy-crypto?coin=${previewCoinData.coinRequestName}`

  return (
    <>
      <DropdownItemGridContainer>
        <ItemWrapper>
          {options.map((item: any) => {
            const withLocale = false
            return (
              <DropdownItem key={item.url} to={item.url} withLocale={withLocale} replace>
                <DropdownItemName>{item.name}</DropdownItemName>
                <DropdownItemText>{t('Find out more about our company')}</DropdownItemText>
              </DropdownItem>
            )
          })}
          {dummyOptions.map(item => (
            <LinkWithLocale
              key={item.coinsRequestName}
              to={
                locale === 'en'
                  ? `${GATSBY_MAIN_URL}/page/buy-crypto?coin=${item.coinsRequestName}`
                  : `${GATSBY_MAIN_URL}/page/${locale}/buy-crypto?coin=${item.coinsRequestName}`
              }
              onMouseEnter={() =>
                setPreviewCoinData({
                  coinPrice: item.estimatedAmount ?? '',
                  coinIcon: item.icon,
                  coinName: item.label,
                  coinTicker: item.tickerLabel,
                  coinRequestName: item.coinsRequestName ?? '',
                  isError: item.isError,
                })
              }>
              <PairItem
                isPositive
                percent={item.percent}
                label={item.label}
                tickerLabel={item.tickerLabel}
                coinsRequestName={item.coinsRequestName}
                trendRange={item.trendRange}
                isBadge={item.isBadge}
                isIndicator={true}
                estimatedAmount={item.estimatedAmount}
                isError={item.isError}
              />
            </LinkWithLocale>
          ))}
        </ItemWrapper>
        <DropdownFooterWrapper />
        <DropdownNavPreviewBlockWrapper
          price={previewCoinData.coinPrice}
          tickerLabel={previewCoinData.coinTicker}
          trendIndex={2.2}
          coinName={previewCoinData.coinName}
          preview={previewCoinData.coinIcon}
          buttonLabel={t('Buy COIN now', { COIN: previewCoinData.coinTicker })}
          href={buyTokenLink}
          isError={previewCoinData.isError}
          isGridArea
        />
      </DropdownItemGridContainer>
    </>
  )
}

export default DropdownBuyCryptoNavItem

const DropdownItemGridContainer = styled.div`
  display: grid;
  grid-template-areas:
    'item item block'
    'item item block'
    'item item block'
    'item item block'
    'item item block'
    'footer footer block';
  background-color: #f3f7fd;
  border-radius: 0.5rem;
`

const ItemWrapper = styled.div`
  display: grid;
  grid-area: item;
  grid-template-columns: 247px 1fr;
  grid-template-rows: max-content;
  grid-auto-rows: max-content;
  grid-column-gap: 2rem;
  grid-row-gap: 4px;
  padding: 2rem;
  background-color: #f3f7fd;
  width: 590px;
  border-radius: 8px 0 0 0;
`

const DropdownItem = styled(LinkWithLocale)`
  display: block;
  position: relative;
  text-align: start;
  width: 300px;
  padding: 0.625rem 0.75rem;
  clear: both;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.normal};
  color: ${({ theme: { colors } }) => colors.dropdownItem};
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  line-height: 1.375;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    > h6 {
      color: ${({ theme: { colors } }) => colors.link};
    }

    text-decoration: none;
    background-color: ${({ theme: { colors } }) => colors.white};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 2rem;
      border-radius: 0.25rem;
      background-image: url(${arrowRightDropdownItem});
      background-repeat: no-repeat;
      background-position: center;
      background-color: ${({ theme: { colors } }) => colors.newLightColor.vividBlue};

      [dir='rtl'] & {
        left: 0;
        right: unset;
        transform: rotate(180deg);
      }
    }
  }
`

const DropdownNavPreviewBlockWrapper = styled(PreviewBlock)`
  grid-area: block;
  background: linear-gradient(180deg, #1743a7 0%, #1743a7 100%);
  border-radius: 0px 8px 8px 0px;
`
const DropdownFooterWrapper = styled(DropdownFooter)`
  grid-area: footer;
  border-radius: 0px 0px 8px 8px;
`

interface IOwnProps {
  name?: string
  options: Array<{ name: string; url: string }>
  warning?: string | null
  type: THeaderType
}
